/**
 * Card -- the one Home/dashboard surface primitive (P124.1 / brand P1.4).
 *
 * Single shape for the four Home cards (cascade / MVB / board / dossier)
 * + signals strips. The brand rule: a calm white card on the warm
 * canvas, with STATUS shown as a 3px inline-start edge + a `StatusPill`
 * -- never a full background fill. Interaction stays brass (hover ring),
 * status stays on the `rag.*` ramp.
 *
 * `cardClasses(status, extra)` is exported so a whole-card <Link> (e.g.
 * CascadeHomeCard) can wear the same look without nesting a <section>
 * inside an <a>. Plain (non-clickable) cards use <Card>.
 *
 * Lives in shared/ per the eslint module-boundary rule.
 */
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { useTranslation } from "react-i18next";

import { ERROR_REFETCH_MS } from "shared/api/queryClient";
import { MarsadIcon } from "shared/components/MarsadIcon";
import type { MarkName } from "shared/icons/marks.generated";

export type RagTone = "red" | "amber" | "green";

// P214.3 (Observatory): the 3px inline-start status edge is RETIRED.
// Status now reads through the StatusPill + the in-card number colour
// (per-card, P214.6). The `status` param is kept for API compatibility
// (callers still pass it) but no longer paints an edge.
const BASE = "rounded-xl border border-paper-2 bg-white px-[26px] py-6 shadow-card";

/** Compose the card class string. Use for a <Link>/<a> that IS the card. */
export function cardClasses(_status?: RagTone | null, extra = ""): string {
  return [BASE, extra].filter(Boolean).join(" ");
}

/** Brass hover affordance for a clickable card. */
export const CARD_HOVER =
  "transition hover:border-brand-brass hover:ring-1 hover:ring-brand-brass/40";

const MICRO_HEADING =
  "font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted";

interface CardProps extends ComponentPropsWithoutRef<"section"> {
  /** Drives the 3px inline-start status edge. Omit for a neutral card. */
  status?: RagTone | null;
  /** Optional mono micro-heading rendered above the children. */
  micro?: ReactNode;
  /** P284.2: the card's subject Marsad Mark, brass beside the micro heading. */
  icon?: MarkName | undefined;
  testid?: string | undefined;
  children: ReactNode;
}

export function Card({
  status = null,
  micro,
  icon,
  testid,
  className = "",
  children,
  ...rest
}: CardProps) {
  return (
    <section data-testid={testid} className={cardClasses(status, className)} {...rest}>
      {micro != null && (
        <p className={`flex items-center gap-1.5 ${MICRO_HEADING}`}>
          {icon && (
            <MarsadIcon name={icon} size={13} className="text-brand-brassDark" />
          )}
          {micro}
        </p>
      )}
      {children}
    </section>
  );
}

/**
 * CardError -- the one calm "load failed" state (P125.2 / brand P2.10).
 *
 * Visible but quiet (a muted line, not a loud rose box) so an admin can
 * tell "no data" from "load failed" without the card shouting on every
 * transient blip. The four Home cards reuse this instead of each
 * inventing their own error rendering (or silently returning null).
 */
export function CardError({
  testid,
  micro,
  icon,
  message,
}: {
  testid?: string;
  micro?: ReactNode;
  icon?: MarkName;
  message: string;
}) {
  const { t } = useTranslation();
  return (
    <Card testid={testid} micro={micro} icon={icon}>
      <p className="mt-2 text-sm text-ink-muted">{message}</p>
      {/* Round 17 K18 -- say when it will try again. */}
      <p data-testid="card-error-retry" className="mt-1 text-xs text-ink-muted">
        {t("common.cardRetrying", { seconds: ERROR_REFETCH_MS / 1000 })}
      </p>
    </Card>
  );
}
